import { isUndefined } from 'lodash';
import { type DataRequestV2, RequestStatus, RequestType } from '~/common/types';
import { hasNoAssetsPresent, isConnectbaseCovered, type UtilityAgency } from '.';

const findRequest = (id: string, type: RequestType, requests: DataRequestV2[]) => {
  return requests.find((r) => r.agency.id === id && r.type === type);
};

interface DataProps {
  agencies: { [agencyId: string]: UtilityAgency };
  dataRequests: { [requestId: string]: DataRequestV2 };
  // Flag-gated: a stored Connectbase route counts like published digital data.
  connectbaseCoverage?: boolean;
}

export function getRequestProgressData({ agencies, dataRequests, connectbaseCoverage = false }: DataProps) {
  let completed = 0;
  let inProgress = 0;
  let requestable = 0;
  let cancelled = 0;

  const agencyIds = Object.keys(agencies);
  const requests = Object.values(dataRequests);

  for (let i = 0; i < agencyIds.length; i++) {
    const id = agencyIds[i];
    const a = agencies[id];

    const mapRequest = findRequest(id, RequestType.MapsRequested, requests);
    const asbuiltRequest = findRequest(id, RequestType.AsbuiltsRequested, requests);

    if (mapRequest || asbuiltRequest) {
      const statuses = [mapRequest?.requestStatus, asbuiltRequest?.requestStatus].filter((s) => !isUndefined(s));

      if (statuses.every((s) => s === RequestStatus.Complete)) {
        completed++;
      } else if (statuses.every((s) => s === RequestStatus.Cancelled || s === RequestStatus.Dismissed)) {
        cancelled++;
      } else {
        inProgress++;
      }
    } else if (a.isDigitized) {
      completed++;
    } else if (hasNoAssetsPresent(a)) {
      completed++;
    } else if (a.availableDigitally || isConnectbaseCovered(a, connectbaseCoverage)) {
      completed++;
    } else {
      requestable++;
    }
  }

  return { completed, inProgress, requestable, cancelled };
}

export function getResearchProgressData({ agencies, dataRequests, connectbaseCoverage = false }: DataProps) {
  let initiated = 0;
  let notStarted = 0;
  const agencyIds = Object.keys(agencies);
  const requests = Object.values(dataRequests);

  for (let i = 0; i < agencyIds.length; i++) {
    const id = agencyIds[i];
    const a = agencies[id];
    const dr = findRequest(id, RequestType.MapsRequested, requests);
    const ar = findRequest(id, RequestType.AsbuiltsRequested, requests);

    // CG-1219: a request-only row exists because a request was made against it, and its
    // availability fields are all defaults — the branches below would read it as not started.
    if (a.isRequestOnly) {
      initiated += 1;
      continue;
    }

    // if as-builts are available and an as-built request has been started,
    // then the request has been initiated;
    if (a.asbuiltsAvailable && !!ar) {
      initiated += 1;
    }
    // else if the agency is data-requestable and a data request exists,
    // then the request has been initiated
    else if (a.dataRequestable && !!dr) {
      initiated += 1;
    }
    // if no requests exist above, but the assets are available on the map
    // we still classify the agency as "initiated"
    else if (a.availableDigitally || isConnectbaseCovered(a, connectbaseCoverage)) {
      initiated += 1;
    }
    // if confirmed NAP, its still "complete"/"initiated"
    else if (hasNoAssetsPresent(a)) {
      initiated += 1;
    } else {
      notStarted += 1;
    }
  }

  return { initiated, notStarted };
}
